import React, { useState, useEffect } from 'react'
import {
    View,
    Text,
    TouchableOpacity,
    StyleSheet,
    Alert,
    Platform
} from 'react-native'
import { Link } from 'expo-router';
import { supabase } from '../lib/supabase'
import type { Papers } from '../lib/supabase'
import { getPaperUrlParams } from '../lib/paperUrl'
import colors from '../lib/colors'
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import { useHydratedTimeZone } from '../hooks/useHydratedTimeZone';

type Paper = Papers & {
    is_private: boolean;
    subfield?: { id: number; name: string; slug: string; field_id: number } | null;
}

interface PaperItemProps {
    item: Paper
    isOwner?: boolean
    showPrivacyToggle?: boolean
}

export default function PaperItem({
    item,
    isOwner = true,
    showPrivacyToggle = false
}: PaperItemProps) {
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const timeZone = useHydratedTimeZone();
    const [isPrivate, setIsPrivate] = useState(item.is_private || false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setIsPrivate(item.is_private || false)
    }, [item.is_private])

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return '#f59e0b'
            case 'completed': return colors.primaryGreen
            case 'failed': return '#ef4444'
            default: return '#6b7280'
        }
    }

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
            case 'failed': return i18n._(msg({ id: 'paper.status.notProcessed', message: 'Not processed' }))
            case 'completed': return i18n._(msg({ id: 'paper.status.completed', message: 'Completed' }))
            default: return status
        }
    }

    const togglePrivacy = async () => {
        if (!isOwner || item.status !== 'completed') return

        setLoading(true)
        const { error } = await supabase
            .from('papers')
            .update({ is_private: !isPrivate })
            .eq('id', item.id)

        if (!error) {
            setIsPrivate(!isPrivate)
        } else {
            Alert.alert(
                i18n._(msg({ id: 'common.error', message: 'Error' })),
                i18n._(msg({ id: 'paper.privacyUpdateFailed', message: 'Could not update privacy setting' }))
            )
        }
        setLoading(false)
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        // ICU versions disagree on spaces in e.g. Spanish "a. m.". Normalize
        // only the formatted timestamp so SSR and browser hydration match.
        return date.toLocaleDateString(formatLocale, {
            timeZone,
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/[\u00a0\u202f]/g, ' ')
    }

    const renderStars = (rating: number) => {
        const stars = []
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <Text key={i} style={styles.star}>
                    {i <= rating ? '★' : '☆'}
                </Text>
            )
        }
        return stars
    }


    return (
        <View style={styles.paperCard}>
            {item.status === 'completed' ? (
                <Link
                    href={getPaperUrlParams(item)}
                    asChild
                >
                    <TouchableOpacity
                        style={styles.mainRow}
                        activeOpacity={0.7}
                    >
                        {/* Content for completed papers */}
                        <View style={styles.contentContainer}>
                            {/* Title and Status Row */}
                            <View style={styles.headerRow}>
                                <Text style={styles.paperTitle} numberOfLines={1} ellipsizeMode="tail">
                                    {item.original_title}
                                </Text>
                            </View>

                            {/* Description */}
                            {item.summary_of_findings && (
                                <Text style={styles.description} numberOfLines={2} ellipsizeMode="tail">
                                    {item.summary_of_findings}
                                </Text>
                            )}

                            {/* Rating */}
                            {item.paperzilla_rating && (
                                <View style={styles.ratingRow}>
                                    {renderStars(item.paperzilla_rating)}
                                </View>
                            )}

                            {/* Footer Row */}
                            <View style={styles.footerRow}>
                                <Text style={styles.fileName} numberOfLines={1} ellipsizeMode="middle">
                                    {item.subfield?.name || ""}
                                </Text>
                                <View style={styles.rightFooter}>
                                    <Text style={styles.timestamp}>{item.created_at ? formatDate(item.created_at) : <Trans id="paperitem.unknown" comment="Conditional UI copy in components/PaperItem.tsx">Unknown</Trans>}</Text>
                                </View>
                            </View>
                        </View>
                    </TouchableOpacity>
                </Link>
            ) : (
                <TouchableOpacity
                    style={styles.mainRow}
                    activeOpacity={1}
                    disabled={true}
                >
                    {/* Content for pending/failed papers */}
                    <View style={styles.contentContainer}>
                        <View style={styles.headerRow}>
                            <Text style={styles.paperTitle} numberOfLines={1} ellipsizeMode="tail">
                                {item.original_title}
                            </Text>
                            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status || 'pending') }]}>
                                <Text style={styles.statusText}>{getStatusText(item.status || 'pending')}</Text>
                            </View>
                        </View>
                        <Text style={styles.description} numberOfLines={2} ellipsizeMode="tail"><Trans id="paperitem.uploadNotProcessed" comment="Unfinished legacy PDF uploads will not be processed or send completion emails.">
                            This paper was not processed. PDF uploads are no longer available.
                        </Trans></Text>
                        <View style={styles.footerRow}>
                            <Text style={styles.fileName} numberOfLines={1} ellipsizeMode="middle">
                                {item.subfield?.name || ""}
                            </Text>
                            <View style={styles.rightFooter}>
                                <Text style={styles.timestamp}>{item.created_at ? formatDate(item.created_at) : <Trans id="paperitem.unknown" comment="Conditional UI copy in components/PaperItem.tsx">Unknown</Trans>}</Text>
                            </View>
                        </View>
                    </View>
                </TouchableOpacity>
            )}

            {/* Privacy Controls (only for owner's completed papers) */}
            {isOwner && showPrivacyToggle && item.status === 'completed' && (
                <View style={styles.privacySection}>
                    <View style={styles.privacyRow}>
                        <View style={styles.privacyInfo}>
                            <Text style={styles.privacyLabel}>
                                {isPrivate ? <Trans id="paperitem.private" comment="Conditional UI copy in components/PaperItem.tsx">🔒 Private</Trans> : <Trans id="paperitem.public" comment="Conditional UI copy in components/PaperItem.tsx">🌐 Public</Trans>}
                            </Text>
                            <Text style={styles.privacyDescription}>
                                {isPrivate
                                    ? <Trans id="paperitem.onlyYouCanSeeThisPaper" comment="Conditional UI copy in components/PaperItem.tsx">Only you can see this paper</Trans>
                                    : <Trans id="paperitem.visibleToOtherResearchers" comment="Conditional UI copy in components/PaperItem.tsx">Visible to other researchers</Trans>
                                }
                            </Text>
                        </View>
                        <TouchableOpacity
                            style={[styles.toggleButton, isPrivate && styles.toggleButtonPrivate]}
                            onPress={togglePrivacy}
                            disabled={loading}
                        >
                            <Text style={[styles.toggleButtonText, isPrivate && styles.toggleButtonTextPrivate]}>
                                {loading ? '...' : (isPrivate ? <Trans id="paperitem.makePublic" comment="Conditional UI copy in components/PaperItem.tsx">Make Public</Trans> : <Trans id="paperitem.makePrivate" comment="Conditional UI copy in components/PaperItem.tsx">Make Private</Trans>)}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    paperCard: {
        backgroundColor: 'white',
        borderRadius: 8,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.05,
            shadowRadius: 2,
            elevation: 1,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }),
    },
    mainRow: {
        flexDirection: 'row',
        padding: 16,
        overflow: 'hidden',
    },
    contentContainer: {
        flex: 1,
        overflow: 'hidden',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 8,
    },
    paperTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        flex: 1,
        marginRight: 12,
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        flexShrink: 0,
        minWidth: 80,
        justifyContent: 'center',
        alignItems: 'center',
    },
    statusText: {
        fontSize: 12,
        fontWeight: '500',
        color: 'white',
    },
    description: {
        fontSize: 14,
        color: '#6b7280',
        lineHeight: 20,
        marginBottom: 12,
    },
    ratingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
    },
    star: {
        fontSize: 16,
        color: '#fbbf24',
        marginRight: 1,
    },
    timestamp: {
        fontSize: 12,
        color: '#9ca3af',
    },
    privacySection: {
        borderTopWidth: 1,
        borderTopColor: '#f3f4f6',
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    privacyRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    privacyInfo: {
        flex: 1,
    },
    privacyLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.primaryGreenDark,
        marginBottom: 2,
    },
    privacyDescription: {
        fontSize: 12,
        color: '#6b7280',
    },
    toggleButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6,
        backgroundColor: colors.veryLightGreen,
        borderWidth: 1,
        borderColor: colors.paleGreen,
    },
    toggleButtonPrivate: {
        backgroundColor: colors.primaryGreen,
        borderColor: colors.primaryGreen,
    },
    toggleButtonText: {
        fontSize: 12,
        fontWeight: '500',
        color: colors.primaryGreenDark,
    },
    toggleButtonTextPrivate: {
        color: 'white',
    },
    footerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    fileName: {
        fontSize: 12,
        color: '#9ca3af',
        marginRight: 12,
        width: 250
    },
    rightFooter: {
        alignItems: 'flex-end',
        flexShrink: 0,
    },
})
